import { Save, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

import { ChartBlock } from '@/_shared/components/ChartBlock';
import { CodeBlock } from '@/_shared/components/CodeBlock';
import { DataTable } from '@/_shared/components/DataTable';
import { StreamingText } from '@/_shared/components/StreamingText';
import { Badge } from '@/_shared/components/ui/badge';
import type { SSEMessage } from '@/_shared/types';

import { ActivityGroup } from './ActivityGroup';
import { AlertBlock } from './AlertBlock';
import { MeasurementPlanBlock } from './MeasurementPlanBlock';
import { RecommendedActions } from './RecommendedActions';
import { SkillInvokedChip } from './SkillInvokedChip';
import { groupMessages, isToolActive } from './tool-call-meta';

interface MessageListProps {
  messages: SSEMessage[];
  thinking?: boolean;
  onAction?: (message: string) => void;
  /** Compact mode for sidebar — reduced padding, no max-width constraint. */
  compact?: boolean;
  /** When false, recommend_actions blocks are hidden (e.g. until the turn closes). Defaults to true. */
  showRecommendActions?: boolean;
  /** Disables recommend_actions buttons (e.g. while a turn is active). */
  actionsDisabled?: boolean;
}

function renderSSEMessage(message: SSEMessage, index: number): React.ReactElement | null {
  switch (message.type) {
    case 'user_message':
      return (
        <div key={`user-${index}`} className="flex justify-end px-4 py-3">
          <div
            className="max-w-[85%] animate-slide-up rounded-2xl border border-border/60 bg-card px-4 py-2.5 text-sm"
            data-testid="user-bubble"
          >
            <StreamingText content={message.content} />
          </div>
        </div>
      );

    case 'assistant':
      // Transient — latest assistant text surfaces via ThinkingIndicator shimmer.
      // The last assistant text before each tool group is captured as group context.
      return null;

    case 'ui_message': {
      if (message.uiType === 'change_title') return null;
      if (message.uiType === 'recommend_actions') return null;
      if (message.uiType === 'measurement_plan') {
        return (
          <div key={`ui-${index}`} className="mx-4 my-2 animate-slide-up">
            <MeasurementPlanBlock content={message.content} />
          </div>
        );
      }

      const inner = (() => {
        switch (message.uiType) {
          case 'table':
            return <DataTable content={message.content} metadata={message.metadata} />;
          case 'code':
            return <CodeBlock content={message.content} metadata={message.metadata} />;
          case 'alert':
            return <AlertBlock content={message.content} metadata={message.metadata} />;
          case 'chart':
            return <ChartBlock content={message.content} metadata={message.metadata} />;
          default:
            return <StreamingText content={message.content} />;
        }
      })();
      return (
        <div key={`ui-${index}`} className="mx-4 my-2 min-w-0 animate-slide-up">
          {inner}
        </div>
      );
    }

    case 'result':
    case 'turn_start':
    case 'turn_complete':
    case 'turn_aborted':
      return null;

    case 'error':
      return (
        <div
          key={`error-${index}`}
          className="mx-4 my-2 flex animate-slide-up items-start gap-2 rounded-lg border border-red-200/50 bg-red-50 p-3 text-sm text-red-700"
        >
          <TriangleAlert size={16} className="mt-0.5 shrink-0" />
          <span>{message.message}</span>
        </div>
      );

    case 'tool_use':
    case 'tool_result':
      return null;

    case 'skill_invoked':
      return <SkillInvokedChip key={`skill-invoked-${index}`} message={message} />;

    case 'memory_flushed':
      return (
        <div key={`memory-flushed-${index}`} className="mx-4 my-1" data-testid="memory-flushed-chip">
          <Badge
            variant="secondary"
            className="gap-1 border-emerald-300/60 bg-emerald-50 text-xs text-emerald-800 dark:border-emerald-700/40 dark:bg-emerald-950/40 dark:text-emerald-200"
          >
            <Save className="size-3" />
            Saved {message.count} {message.count === 1 ? 'fact' : 'facts'} before summarizing
          </Badge>
        </div>
      );

    case 'compaction_started':
      return (
        <div key={`compact-start-${index}`} className="mx-4 my-1" data-testid="compaction-started-chip">
          <span className="text-xs text-muted-foreground italic">Summarizing earlier turns...</span>
        </div>
      );
  }
}

function truncateThinking(text: string, max = 200): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}

function ThinkingIndicator({ text }: { text: string }): React.ReactElement {
  return (
    <div className="mx-4 my-1 animate-slide-up" data-testid="thinking-indicator">
      <span className="animate-shimmer text-sm text-muted-foreground" role="status">
        {truncateThinking(text)}
      </span>
    </div>
  );
}

export function MessageList({
  messages,
  thinking,
  onAction,
  compact = false,
  showRecommendActions = true,
  actionsDisabled = false,
}: MessageListProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (isNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const lastAssistantText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg?.type === 'assistant') return msg.content;
      if (msg?.type === 'ui_message' || msg?.type === 'error' || msg?.type === 'user_message') break;
    }
    return null;
  }, [messages]);

  const grouped = groupMessages(messages);
  const toolActive = isToolActive(messages);

  const thinkingText = thinking ? truncateThinking(lastAssistantText ?? 'Thinking…') : undefined;

  // Find the index of the last recommend_actions block — only that one should
  // be rendered, and only if no subsequent turn has started after it.
  let lastRecommendIdx = -1;
  let hasUserMessageAfterLastRecommend = false;
  for (let i = grouped.length - 1; i >= 0; i--) {
    const g = grouped[i]!;
    if (g.kind === 'single' && g.message.type === 'ui_message' && g.message.uiType === 'recommend_actions') {
      lastRecommendIdx = i;
      break;
    }
    // If there's a user_message after the last recommend_actions, a new turn started
    if (g.kind === 'single' && g.message.type === 'user_message') {
      hasUserMessageAfterLastRecommend = true;
    }
  }

  const renderItems: React.ReactElement[] = [];
  for (let i = 0; i < grouped.length; i++) {
    const group = grouped[i]!;
    const isLastGroup = i === grouped.length - 1;
    const isActiveGroup = isLastGroup && toolActive;

    if (group.kind === 'activity') {
      renderItems.push(
        <ActivityGroup
          key={`activity-${group.startIndex}`}
          items={group.items}
          isActive={isActiveGroup}
          context={group.context}
          thinkingText={isActiveGroup ? thinkingText : undefined}
        />,
      );
    } else if (group.message.type === 'ui_message' && group.message.uiType === 'recommend_actions') {
      // Only show recommend_actions for the last completed turn — hide if:
      // - showRecommendActions is false
      // - this isn't the last recommend_actions block
      // - a new turn started after this block (user sent another message)
      // - agent is currently active (thinking)
      if (!showRecommendActions) continue;
      if (i !== lastRecommendIdx) continue;
      if (hasUserMessageAfterLastRecommend) continue;
      if (thinking) continue;
      renderItems.push(
        <div key={`actions-${group.index}`} className="mx-4 my-2 animate-slide-up">
          <RecommendedActions
            content={group.message.content}
            metadata={group.message.metadata}
            onAction={onAction}
            disabled={actionsDisabled}
          />
        </div>,
      );
    } else {
      const el = renderSSEMessage(group.message, group.index);
      if (el) renderItems.push(el);
    }
  }

  // Show standalone thinking indicator only when no tool groups exist yet
  const showThinking = thinking && !toolActive;

  return (
    <div ref={containerRef} className="min-h-0 flex-1 overflow-y-auto" role="log" aria-live="polite">
      <div className={compact ? 'py-3' : 'mx-auto max-w-4xl py-6'}>
        {renderItems.length === 0 && !compact && (
          <div className="flex h-full items-center justify-center py-32 text-center text-muted-foreground">
            <div>
              <div className="mb-2 text-lg">Rudder AI</div>
              <div className="mb-2 text-sm">Ask about your data pipeline, warehouse health, or PR reviews.</div>
              <span className="inline-block rounded-full border border-brand/20 bg-brand/8 px-2.5 py-0.5 text-[0.625rem] font-semibold tracking-wider text-brand uppercase">
                Private Beta
              </span>
            </div>
          </div>
        )}
        {renderItems}
        {showThinking && <ThinkingIndicator text={thinkingText ?? 'Thinking…'} />}
      </div>
    </div>
  );
}
